import React, { useState, useContext } from 'react'
import classNames from 'classnames'
import { Button, Heading } from '@toasttab/shared/src/components'
import Description from '@toasttab/shared/src/components/atoms/Description/Description'
import Eyebrow from '@toasttab/shared/src/components/molecules/Eyebrow/Eyebrow'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import MainForm from '@toasttab/otl/src/components/Form/MainForm/MainForm'
import GridLayout from '@toasttab/shared/src/components/organisms/Grid/GridLayout/GridLayout'
import GridColumn from '@toasttab/shared/src/components/organisms/Grid/GridColumn/GridColumn'
import Image from '@toasttab/shared/src/components/atoms/Image/Image'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { OTLContext } from '@toasttab/shared/src/context/OTLContext'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import { resolvePrefix } from '@toasttab/shared/src/utils/assetUtils'
import { GatedCTALongFormProps } from './GatedCTALongForm.d'

const GatedCTALongForm = ({
  entryId,
  typeHandle,
  gatedLabel,
  gatedLabelIcon,
  gatedLink,
  gatedText,
  gatedThumbnail,
  gatedThumbnailDarkTheme,
  gatedTitle,
  marketoFormId,
  marketoProgramName,
  formFieldsets,
  ctaThankYouLink,
  backgroundColor = 'white',
  variation,
  displayOnSidebar = false
}: GatedCTALongFormProps) => {
  const { t } = useI18next()
  const otlParams = useContext(OTLContext)
  const OTLArticleUX: boolean = Boolean(otlParams.isArticle)
  const isMinimal: boolean = Boolean(otlParams.isMinimal)

  // dynamically create initialValues for Formik
  const formCategory = formFieldsets && formFieldsets[0]
  const [formToggle, setFormToggle] = useState<boolean>(false)

  const eyebrowIcon = {
    src: gatedLabelIcon && gatedLabelIcon[0]?.url,
    position: 'left',
    width: '14px',
    height: '14px'
  }

  const handleFormToggle = () => {
    setFormToggle(true)
  }

  const step1Css = classNames({
    hidden: formToggle
  })

  const step2Css = classNames({
    hidden: !formToggle
  })

  const eyebrowCss = classNames({
    'text-default text-xs tracking-[0.1em] mb-4': true
  })

  const renderButton = (
    fullWidth: boolean,
    capText: boolean = false,
    gatedTitle: string
  ) => {
    return (
      <Button
        arrowHover
        variation={
          variation === 'orange-illustration' ? 'secondary-otl' : 'primary-otl'
        }
        fullwidth={fullWidth}
        onClick={handleFormToggle}
        download
      >
        <span
          className={`font-semibold ${
            capText && !OTLArticleUX ? 'uppercase' : 'leading-6 md:leading-4'
          }`}
        >
          {t('Get Free Download')}
        </span>
      </Button>
    )
  }

  const wrapperCss = classNames({
    'px-4 py-6': true,
    'bg-white': backgroundColor === 'white',
    'bg-otl-tan-dark': backgroundColor === 'otl-tan-dark'
  })

  const OrangeIllustrationImageWrapperCssClass = classNames({
    'h-auto bg-toast-orange-500 relative overflow-visible bg-img-resource':
      true,
    'rounded-tl-lg md:max-xl:rounded-tl-none rounded-tr-lg rounded-bl-none md:rounded-tr-none xl:rounded-bl-lg\
      bg-[left_calc(-70px)_top_calc(-100px)] md:bg-[left_calc(-100px)_top_calc(-20px)]':
      !OTLArticleUX,
    'rounded-t-lg md:rounded-tr-none TechTips_QuizTechTips_QuizTechTips_QuizTechTips_QuizTechTips_QuizTechTips_QuizTechTips_Quizmd:rounded-l-lg md:rounded-bl-lg md:bg-[left_calc(-100px)_top_calc(-60px)] min-w-[190px] bg-[left_calc(-36px)_top_calc(-100px)]':
      OTLArticleUX,
    'md:min-w-[12px] xl:min-w-[190px]': !displayOnSidebar && !OTLArticleUX,
    'min-w-[90px]': displayOnSidebar
  })

  return (
    <>
      {variation === 'orange-illustration' && (
        <section
          className={`otl-gated-cta-long-form drop-shadow-lg md:mb-0 ${
            !OTLArticleUX ? 'xl:-ml-[50px]' : ''
          } ${isMinimal ? 'hidden md:block' : ''}`}
        >
          <div
            className={
              'rounded-lg md:rounded-tl-none md:rounded-bl-none xl:rounded-lg flex flex-col md:flex-row bg-white overflow-hidden'
            }
          >
            <div className={OrangeIllustrationImageWrapperCssClass}>
              <div className='w-full h-[180px] md:h-full relative overflow-hidden'></div>
            </div>
            <div className={`p-6 md:p-10 w-full ${step1Css}`}>
              <Eyebrow
                icon={eyebrowIcon}
                componentClass={eyebrowCss}
                variation={'otl-black'}
              >
                {gatedLabel}
              </Eyebrow>
              {gatedTitle && (
                <Heading
                  id=''
                  classes={`${!OTLArticleUX ? 'mb-2' : 'mb-4'}`}
                  color={['text-black']}
                  fontFamily='font-calibre'
                  fontWeight='font-semibold'
                  letterSpacing='tracking-normal'
                  textAlign={['text-left']}
                  width='w-full'
                  fontSize={['text-otl-cta-heading']}
                  leading={'leading-[110%]'}
                  content={gatedTitle}
                  heading='h3'
                />
              )}
              <Typography
                as='p'
                variation='default'
                classes='font-sansPro text-lg text-default mb-4'
                allowHtml={false}
              >
                {gatedText}
              </Typography>
              {!OTLArticleUX && (
                <Description variation='otl-light-sansPro' classes='mb-4'>
                  {gatedText}
                </Description>
              )}
              <div className='md:hidden block mb-2'>
                {renderButton(true, true, `${OTLArticleUX ? gatedTitle : ''}`)}
              </div>
              <div className='md:block hidden'>
                {renderButton(false, true, `${OTLArticleUX ? gatedTitle : ''}`)}
              </div>
              <div className='mt-8'>
                <img
                  alt='Toast'
                  className='mr-4'
                  src={resolvePrefix('/svg/img-toast-cta-branding.svg')}
                />
              </div>
            </div>
            <div className={`p-6 md:p-10 w-full ${step2Css}`}>
              <Eyebrow
                icon={eyebrowIcon}
                componentClass={eyebrowCss}
                variation={'otl-black'}
              >
                {gatedLabel}
              </Eyebrow>
              {gatedTitle && (
                <Heading
                  id=''
                  classes={`${!OTLArticleUX ? 'mb-2' : 'mb-4'}`}
                  color={['text-black']}
                  fontFamily='font-calibre'
                  fontWeight='font-semibold'
                  letterSpacing='tracking-normal'
                  textAlign={['text-left']}
                  width='w-full'
                  fontSize={['text-otl-cta-heading']}
                  leading={'leading-[110%]'}
                  content={gatedTitle}
                  heading='h3'
                />
              )}
              <Typography
                as='p'
                variation='default'
                classes='font-sansPro text-lg text-default mb-4'
                allowHtml={false}
              >
                {gatedText}
              </Typography>

              <MainForm
                entryId={entryId}
                typeHandle={typeHandle}
                formId={marketoFormId}
                programName={marketoProgramName}
                formCategory={formCategory}
                redirectUrl={ctaThankYouLink}
                buttonText='Get a free download'
                buttonStyle='secondary-otl'
                conversionType='MEL-HEADLESS'
              />
              <div className='mt-8'>
                <img
                  alt='Toast'
                  className='mr-4'
                  src={resolvePrefix('/svg/img-toast-cta-branding.svg')}
                />
              </div>
            </div>
          </div>
        </section>
      )}
      {variation === 'default' && (
        <section className='otl-gated-cta-long-form -mx-5 md:mx-0 md:mb-0'>
          <div className={wrapperCss}>
            <GridLayout column='grid-cols-3' classes={`${step1Css}`}>
              {gatedThumbnail && (
                <GridColumn
                  columnSpan={['col-span-1']}
                  classes='hidden md:block'
                >
                  <Image {...gatedThumbnail[0]} />
                </GridColumn>
              )}
              <GridColumn columnSpan={['col-span-2']}>
                <div>
                  <Eyebrow icon={eyebrowIcon} variation={'otl-black'}>
                    {gatedLabel}
                  </Eyebrow>
                  {gatedTitle && (
                    <Heading
                      id=''
                      classes='mb-[5px]'
                      color={['text-black']}
                      fontFamily='font-calibre'
                      fontWeight='font-semibold'
                      letterSpacing='tracking-normal'
                      textAlign={['text-left']}
                      width='w-full'
                      fontSize={['text-3xl']}
                      content={gatedTitle}
                      heading='h3'
                    />
                  )}
                  <Description
                    variation='otl-light-sansPro'
                    classes='pt-[5px] mb-3'
                  >
                    {gatedText}
                  </Description>
                  <div className='md:hidden block'>
                    {renderButton(true, true, gatedTitle)}
                  </div>
                  <div className='md:block hidden'>
                    {renderButton(false, true, gatedTitle)}
                  </div>
                </div>
              </GridColumn>
            </GridLayout>
            <GridLayout column='grid-cols-1' classes={`${step2Css}`}>
              <GridColumn columnSpan={['col-span-1']}>
                <div>
                  <Eyebrow icon={eyebrowIcon} variation={'otl-black'}>
                    {gatedLabel}
                  </Eyebrow>
                  {gatedTitle && (
                    <Heading
                      id=''
                      classes='mb-3'
                      color={['text-black']}
                      fontFamily='font-calibre'
                      fontWeight='font-semibold'
                      letterSpacing='tracking-normal'
                      textAlign={['text-left']}
                      width='w-full'
                      fontSize={['text-3xl']}
                      content={gatedTitle}
                      heading='h3'
                    />
                  )}
                  <MainForm
                    entryId={entryId}
                    typeHandle={typeHandle}
                    formId={marketoFormId}
                    programName={marketoProgramName}
                    formCategory={formCategory}
                    redirectUrl={ctaThankYouLink}
                    buttonStyle='secondary-otl'
                    buttonText='Get free download'
                    conversionType='MEL-HEADLESS'
                  />
                </div>
              </GridColumn>
            </GridLayout>
          </div>
          <div className='p-4 bg-toast-gray-0 flex justify-center'>
            <img
              alt='Toast'
              className='mr-4'
              src={resolvePrefix('/svg/img-toast-logo-gray.svg')}
            />
            <span>{t('Built for Restaurants')}</span>
          </div>
        </section>
      )}
    </>
  )
}

export default GatedCTALongForm
